import React, { useEffect, useImperativeHandle, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { setDeliverPosition } from '../../actions/orderActions'
import CustomerInfo from '../../components/desktop/form-submit'
import { PAYMENT_METHOD } from '../../constants'
import useDidMountEffect from '../../hooks/useDidMountEffect'
import { getListAddress } from '../../selectors'
import { getDeliverInfo } from '../../selectors/order'
import { getDeliveryTime, getOffsetTop, makeAddressCalcDistance, calcMoneyFoodList, formatMoney } from '../../utils'
import { convertErrorYup } from '../../utils/helper'
import { customerInfoValid } from '../../validation'
import { sendRequest } from '../../services/apiService'
import apiConfig from '../../constants/apiConfig'

const defaultForm = {}

const CustomerInfoContainer = (
    { handleCalcDistance, onVoucherStateChange },
    ref
) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const updateFromForm = useRef(false)
    const user = useSelector(state => state.AccountReducer.user)
    const listAddress = useSelector(getListAddress)
    const deliverInfo = useSelector(getDeliverInfo)
    const ordering = useSelector(state => state.OrderReducer.ordering)
    const { name, distance, ...rest } = deliverInfo || {}

    const listDeliveryTimes = getDeliveryTime()

    const [errors, setErrors] = useState({})
    const [voucherVerified, setVoucherVerified] = useState(false)
    const [voucherVerifying, setVoucherVerifying] = useState(false)
    const [voucherId, setVoucherId] = useState('')
    const [voucherData, setVoucherData] = useState(null)
    const [voucherError, setVoucherError] = useState('')
    const [form, setForm] = useState({
        paymentMethod: PAYMENT_METHOD.CASH,
        ...defaultForm,
        ...user,
        ...rest,
        discountCode: '',
        deliveryTime: listDeliveryTimes[0] || '',
    })

    const handleVerifyVoucher = async () => {
        const code = form.discountCode?.trim()
        if (!code) return

        setVoucherVerifying(true)
        try {
            const config = {
                ...apiConfig.voucher.getByCode,
                path: apiConfig.voucher.getByCode.path.replace(':code', code),
            }
            const response = await sendRequest(config)
            if (response.success && response.responseData?.result) {
                const voucherData = response.responseData?.data
                const totalOrdering = calcMoneyFoodList(ordering).total
                const minOrderValue = voucherData?.minOrderValue

                if (minOrderValue && totalOrdering < minOrderValue) {
                    setVoucherVerified(false)
                    setVoucherId('')
                    setVoucherData(null)
                    setVoucherError(
                        t('voucher.minOrderNotMet', {
                            minValue: formatMoney(minOrderValue),
                        })
                    )
                } else {
                    setVoucherVerified(true)
                    setVoucherId(voucherData?.id || '')
                    setVoucherData(voucherData)
                    setVoucherError('')
                }
            } else {
                setVoucherVerified(false)
                setVoucherId('')
                setVoucherData(null)
                setVoucherError(t('voucher.invalidVoucher'))
            }
        } catch {
            setVoucherVerified(false)
            setVoucherId('')
            setVoucherData(null)
            setVoucherError(t('voucher.invalidVoucher'))
        }
        setVoucherVerifying(false)
    }

    const handleInputChange = (e, withoutCalcDistance) => {
        updateFromForm.current = true
        const { name, value } = e.target

        if (name === 'discountCode' && (voucherVerified || voucherError)) {
            setVoucherVerified(false)
            setVoucherId('')
            setVoucherData(null)
            setVoucherError('')
        }

        if (name === 'selectAddress') {
            let address = listAddress?.[value]
            if (!address) {
                return setForm(prev => ({
                    ...prev,
                    userStreet: '',
                    userAddressNumber: '',
                    userCity: '',
                    userPostcode: '',
                    userPhone: '',
                    floor: '',
                    [name]: value,
                }))
            }

            setForm(prev => {
                const userAddressNumber =
                    address.addressNumber || prev.userAddressNumber
                const userStreet = address.street || prev.userStreet
                const userCity = address.city || prev.userCity
                const userPostcode = address.zipCode || prev.userPostcode;

                !withoutCalcDistance &&
                    handleAddressBlur({
                        userAddressNumber,
                        userStreet,
                        userCity,
                        userPostcode,
                    })

                return {
                    ...prev,
                    userStreet,
                    userAddressNumber,
                    userCity,
                    userPostcode: address.zipCode || prev.userPostcode,
                    floor: address.floor || prev.floor,
                    userPhone:
                        address.phone ||
                        prev.userPhone ||
                        user?.userPhone ||
                        '',
                    [name]: value,
                }
            })

            setErrors(prev => ({
                ...prev,
                userStreet: '',
                userAddressNumber: '',
                userCity: '',
                userPostcode: '',
                userPhone: '',
            }))
            return
        }

        setForm(prev => ({ ...prev, [name]: value }))
    }

    const handleSubmit = async e => {
        e && e.preventDefault()
    }

    const clearErrorField = field =>
        setErrors(prev => ({ ...prev, [field]: '' }))

    const handleValidateForm = async () => {
        // validate
        try {
            await customerInfoValid.validate(form, { abortEarly: false })
            return form
        } catch (error) {
            if (error?.name === 'ValidationError') {
                const errors = convertErrorYup(error)
                if (Object.keys(errors).length) {
                    const errorsPosition = Object.keys(errors)
                        .map(el => {
                            const elmnt = document.getElementById(
                                `cusInfo-${el}`
                            )

                            return elmnt && getOffsetTop(elmnt) - 40
                        })
                        .filter(el => el !== false)

                    const errorPosition = Math.min(...errorsPosition)
                    const scrollEl = document.querySelector('#root')
                    scrollEl && (scrollEl.scrollTop = errorPosition)
                }
                setErrors(errors)
            }
        }
        return undefined
    }

    const resetForm = () => {
        setForm({})
    }

    const handleAddressBlur = user => {
        const { userAddressNumber, userStreet, userCity, userPostcode } = user || form
        console.log({ userAddressNumber, userStreet, userCity })
        userAddressNumber &&
            userStreet &&
            userCity &&
            userPostcode &&
            handleCalcDistance(makeAddressCalcDistance(userStreet, userAddressNumber, userCity, userPostcode))
    }

    useEffect(() => {
        onVoucherStateChange?.(voucherVerified)
    }, [voucherVerified, onVoucherStateChange])

    useImperativeHandle(
        ref,
        () => ({
            validateForm: handleValidateForm,
            getUserAddress: () => {
                const { userAddressNumber, userStreet, userCity, userPostcode } = form
                return { userAddressNumber, userStreet, userCity, userPostcode }
            },
            resetForm: resetForm,
            validAddress: () => {
                const { userAddressNumber, userStreet, userCity } = form

                return userAddressNumber && userStreet && userCity
            },
            setErrors: setErrors,
            voucherState: {
                code: form.discountCode,
                verified: voucherVerified,
                id: voucherId,
            },
        }),
        [form, voucherVerified, voucherId]
    )

    useEffect(() => {
        const timeOutSetToRedux =
            updateFromForm.current &&
            setTimeout(() => {
                dispatch(setDeliverPosition(form))
            }, 350)

        return () => {
            timeOutSetToRedux && clearTimeout(timeOutSetToRedux)
        }
    }, [form])

    useDidMountEffect(() => {
        if (!user) {
            return
        }

        setForm(prev => ({
            ...prev,
            ...user,
            deliveryTime: listDeliveryTimes[0] || '',
        }))
        setErrors({})

        handleAddressBlur(user)
    }, [user])

    useDidMountEffect(() => {
        if (deliverInfo.selectAddress !== form.selectAddress) {
            handleInputChange(
                {
                    target: {
                        value: deliverInfo.selectAddress,
                        name: 'selectAddress',
                    },
                },
                true
            )
        }
    }, [deliverInfo.selectAddress])

    useEffect(() => {
        const { name, distance, ...rest } = deliverInfo
        updateFromForm.current = false
        setForm(prev => ({
            ...prev,
            ...rest,
        }))
    }, [deliverInfo])

    useEffect(() => {
        if (distance !== 0 && !distance) {
            handleAddressBlur()
        }
        // handleValidateForm()
    }, [])

    return (
        <CustomerInfo
            handleSubmit={handleSubmit}
            form={form}
            handleInputChange={handleInputChange}
            clearErrorField={clearErrorField}
            errors={errors}
            handleAddressBlur={handleAddressBlur}
            onVerifyVoucher={handleVerifyVoucher}
            voucherVerified={voucherVerified}
            voucherVerifying={voucherVerifying}
            voucherData={voucherData}
            voucherError={voucherError}
        />
    )
}

export default React.forwardRef(CustomerInfoContainer)
